'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import { Link, useRouter } from '@/i18n/navigation';
import { ShieldCheck } from '@phosphor-icons/react';
import { Logo } from '@/components/Logo';

function ActivateContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Pre-fill code from URL param (e.g. /activate?code=EARLYPAW)
  // KAN-280: Show success message when email was confirmed cross-device
  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode) {
      setCode(urlCode);
    }
    const msg = searchParams.get('message');
    if (msg === 'email_confirmed') {
      setMessage('Email confirmed! Please log in to continue.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!code.trim()) {
      setError('Please enter your activation code.');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/activate/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });

      const data = await res.json();

      if (data.valid) {
        // Cookie is set by the API — redirect to signup
        router.push('/signup');
      } else {
        setError(data.error || 'Invalid code. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 w-full max-w-sm mx-auto">
      {/* Logo */}
      <div className="flex justify-center">
        <Logo variant="combined-stacked" size="md" />
      </div>

      {/* Header */}
      <div className="text-center">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-dark mb-2">
          Enter your activation code
        </h1>
        <p className="text-gray-500 text-sm">
          Paste the code from your invite email to create your account.
        </p>
      </div>

      {/* Status messages */}
      {message && (
        <div className="p-3 bg-success-light border border-green-200 rounded-brand text-success text-sm">
          {message}
        </div>
      )}
      {error && (
        <div className="p-3 bg-error-light border border-red-200 rounded-brand text-error text-sm">
          {error}
        </div>
      )}

      {/* Code form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="activation-code" className="block text-sm font-medium text-gray-600 mb-1.5">
            Activation code
          </label>
          <input
            id="activation-code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. EARLYPAW"
            autoComplete="off"
            autoFocus
            className="w-full px-4 py-3 border border-border rounded-brand focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-surface text-dark min-h-[48px] font-mono text-lg tracking-wider text-center uppercase"
            disabled={loading}
          />
        </div>

        <button
          type="submit"
          disabled={loading || !code.trim()}
          className="w-full py-3 px-4 bg-teal-500 text-white font-heading font-semibold rounded-brand hover:bg-teal-600 disabled:bg-gray-400 transition-colors min-h-[48px]"
        >
          {loading ? 'Checking...' : 'Activate'}
        </button>
      </form>

      {/* Trust shield */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
        <ShieldCheck weight="regular" className="w-4 h-4 text-teal-500" />
        <span>Data stored in the EU</span>
      </div>

      {/* Back to waitlist */}
      <div className="text-center text-sm">
        <span className="text-gray-500">Don&apos;t have a code? </span>
        <Link href="/" className="text-teal-500 font-medium hover:text-teal-600">
          Join the waitlist
        </Link>
      </div>

      {/* Already have account */}
      <div className="text-center text-sm">
        <span className="text-gray-500">Already have an account? </span>
        <Link href="/login" className="text-teal-500 font-medium hover:text-teal-600">
          Log in
        </Link>
      </div>
    </div>
  );
}

export default function ActivatePage() {
  return (
    <Suspense fallback={<div className="text-center text-gray-400">Loading...</div>}>
      <ActivateContent />
    </Suspense>
  );
}
